import React, { useEffect, useState } from "react";
import { useAuth } from "../components/auth/AuthContext";
import { useTranslation } from "react-i18next";
import {
  getNotificationHistory,
  dismissNotification,
} from "../api/notificationsApi";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { Hero } from "../components/adminSurface/AdminSurface";
import { BellArt } from "../components/adminSurface/heroArt";

export default function NotificationsPage() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getNotificationHistory(token);
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleMarkRead = async (n) => {
    const id = n.id || n.Id;
    if (!id || !token) return;
    try {
      await dismissNotification(token, id);
      setItems((prev) => prev.filter((x) => (x.id || x.Id) !== id));
    } catch {
      // keep item on error
    }
  };

  const renderNotificationCard = (n) => {
    const id = n.id || n.Id;
    const rawMessage = n.message || n.Message || "";

    // Extract trailing location in parentheses, e.g. "(... (City, Country))".
    let location = null;
    let baseMessage = rawMessage;
    const locMatch = rawMessage.match(/\(([^)]+)\)\s*$/);
    if (locMatch && locMatch[1]) {
      location = locMatch[1];
      baseMessage = rawMessage.replace(/\s*\([^)]*\)\s*$/, "");
    }

    const createdRaw = n.createdAtUtc || n.CreatedAtUtc;
    const createdText = createdRaw
      ? new Date(createdRaw).toLocaleString()
      : null;

    const title = n.title || n.Title || "";
    const category = n.category || n.Category;
    const isLogin = n.referenceType === "login" || n.ReferenceType === "login";

    const mapUrl = location
      ? `https://www.google.com/maps?q=${encodeURIComponent(
          location
        )}&output=embed`
      : null;

    return (
      <div
        key={id}
        className={mapUrl ? "notif-card notif-card--with-map" : "notif-card"}
      >
        <div className="notif-main">
          <div className="notif-title-row">
            <span className="notif-pill">{title || "Notification"}</span>
            {category && <span className="notif-category">{category}</span>}
          </div>
          {baseMessage && <div className="notif-message">{baseMessage}</div>}
          {location && <div className="notif-location-text">{location}</div>}
          {createdText && (
            <div className="notif-meta-row">
              <span className="notif-time">{createdText}</span>
              {isLogin && (
                <span className="notif-tag">Security · Login event</span>
              )}
            </div>
          )}
        </div>
        {mapUrl && (
          <div className="notif-map-embed">
            <iframe
              src={mapUrl}
              title={`Location map: ${location}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
        <div className="notif-actions">
          <button
            type="button"
            className="notif-dismiss-btn"
            onClick={() => handleMarkRead(n)}
          >
            <span className="visually-hidden">
              {t("common.dismiss") || "Dismiss"}
            </span>
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />
      <div className="page-container">
        <div className="notifications-page">
          <Hero
            title={t("user.notifications")}
            subtitle="Recent security activity and system messages for your account."
            art={<BellArt />}
          />

          {loading && (
            <div className="notifications-status">
              {t("common.loading") || "Loading…"}
            </div>
          )}
          {error && <div className="notifications-status error">{error}</div>}
          {!loading && items.length === 0 && (
            <div className="notifications-status empty">
              {t("user.noNotifications")}
            </div>
          )}

          <div className="notifications-list">
            {items.map((n) => renderNotificationCard(n))}
          </div>
        </div>
      </div>
    </div>
  );
}
